import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "@radix-ui/react-slot"

import { cn } from "../../../lib/utils"

const paragraphVariants = cva("font-sans", {
  variants: {
    size: {
      xs: "text-xs",
      sm: "text-sm",
      base: "text-base",
      lg: "text-lg",
      xl: "text-xl",
    },
    weight: {
      normal: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
    },
    leading: {
      none: "leading-none",
      tight: "leading-tight",
      snug: "leading-snug",
      normal: "leading-normal",
      relaxed: "leading-relaxed",
      loose: "leading-loose",
    },
    variant: {
      default: "text-foreground",
      muted: "text-muted-foreground",
      accent: "text-primary",
      destructive: "text-destructive",
      success: "text-green-600 dark:text-green-500",
      warning: "text-yellow-600 dark:text-yellow-500",
    },
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
      justify: "text-justify",
    },
  },
  defaultVariants: {
    size: "base",
    weight: "normal",
    leading: "relaxed",
    variant: "default",
    align: "left",
  },
})

export interface ParagraphProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof paragraphVariants> {
  /**
   * Render as a different element using Radix Slot
   */
  asChild?: boolean
  /**
   * The HTML element to render as
   * @default "p"
   */
  as?: "p" | "span" | "div"
}

/**
 * Paragraph component for body text, descriptions, and general content.
 */
function Paragraph({
  className,
  size,
  weight,
  leading,
  variant,
  align,
  asChild = false,
  as = "p",
  children,
  ...props
}: ParagraphProps) {
  const Comp = asChild ? Slot : as

  return (
    <Comp
      data-slot="paragraph"
      data-size={size}
      data-variant={variant}
      className={cn("text-balance", paragraphVariants({ size, weight, leading, variant, align }), className)}
      {...props}
    >
      {children}
    </Comp>
  )
}

Paragraph.displayName = "Paragraph"

export { Paragraph }
